import './HcpReferral.scss';

import { FormikProvider, useFormik } from 'formik';
import { useRef, useState } from 'react';

import Footer from '@components/Footer/Footer';
import HeaderNav from '@components/Header/HeaderNav';
import { BridgeBioMaterialCode } from '@components/marketing/BridgeBioMaterialCode/BridgeBioMaterialCode';
import { ProcessBand, type ProcessBandStep } from '@components/ProcessBand/ProcessBand';
import { BRIDGE_BIO_CAMPAIGN_CODE, BRIDGE_BIO_LOGO_SRC } from '@constants/bridgeBio';
import { useHeaderHeight } from '@hooks/useHeaderHeight';
import useToast from '@hooks/useToast';
import { createFVTReferral } from '@store/actions/referrals';
import { convertYesNoToBoolean } from '@utils/forms';
import { recordGoogleAnalyticsEvent } from '@utils/helpers';

import { Hero } from './components/Hero';
import { IntakeForm } from './components/IntakeForm';
import { SubmissionSuccess } from './components/SubmissionSuccess';
import { getInitialValues, type HcpReferralFormValues, hcpReferralSchema } from './HcpReferral.validation';

type PageState = { kind: 'normal' } | { kind: 'success'; submittedAt: Date; patientEmail: string; filename: string };

const PROCESS_STEPS: ProcessBandStep[] = [
  {
    label: "01 · You're here",
    title: 'Submit the referral',
    description: 'Patient email + a PDF of the genetic testing / clinical report.',
    timing: '~3 minutes',
    state: 'current',
  },
  {
    label: '02',
    title: 'Internal review',
    description:
      "Our curators verify eligibility and the variant context. We'll contact you first if anything is unclear.",
    timing: '3-5 business days',
    state: 'upcoming',
  },
  {
    label: '03',
    title: 'Patient invitation',
    description: 'If eligible, we email an invitation. The patient opts in on their own timeline.',
    timing: 'Same day as eligibility confirmation',
    state: 'upcoming',
  },
  {
    label: '04',
    title: 'No-charge testing & genetic counseling',
    description:
      'No-charge cheek swab kit + a session with a board certified genetic counselor for referred family members being tested through Family Cascade Genetic Testing - not for those with an existing test result.',
    timing: 'After patient opt-in',
    state: 'upcoming',
  },
];

export const HcpReferral = () => {
  const [pageState, setPageState] = useState<PageState>({ kind: 'normal' });
  const { addToast } = useToast();
  // HeaderNav is rendered as a sibling above the scroll container; the scroll container
  // sizes itself to `calc(100vh - headerHeight)` so the page scrolls below the sticky header.
  const headerHeight = useHeaderHeight();
  // `.App` has `overflow-y: hidden; height: 100vh` on desktop — the page scroll container
  // is `.hcp-referral-page` itself, so we need a ref to scroll IT (not the document) when
  // we transition into the success state.
  const pageRef = useRef<HTMLDivElement | null>(null);

  const scrollPageToTop = () => {
    const el = pageRef.current;
    if (!el) return;
    el.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (values: HcpReferralFormValues) => {
    if (!values.report_pdf) {
      return;
    }

    try {
      await createFVTReferral({
        campaign_code: BRIDGE_BIO_CAMPAIGN_CODE,
        email: values.patient_email,
        first_name: values.patient_first_name.trim() || null,
        last_name: values.patient_last_name.trim() || null,
        provider_first_name: values.hcp_first_name.trim() || null,
        provider_last_name: values.hcp_last_name.trim() || null,
        provider_email: values.hcp_email.trim() || null,
        report_pdf: values.report_pdf,
        eligibility: values.eligibility,
        clinical_history: {
          low_blood_calcium: convertYesNoToBoolean(values.clinical_history.low_blood_calcium),
          elevated_urine_calcium: convertYesNoToBoolean(values.clinical_history.elevated_urine_calcium),
          adh1_symptoms: convertYesNoToBoolean(values.clinical_history.adh1_symptoms),
          adh1_diagnosis: convertYesNoToBoolean(values.clinical_history.adh1_diagnosis),
        },
      });

      recordGoogleAnalyticsEvent('User Interaction', 'Form Submit', 'BridgeBio FVT HCP Intake');

      // LIMS de-duplicates server-side and always acknowledges, so every successful
      // submit lands here.
      setPageState({
        kind: 'success',
        submittedAt: new Date(),
        patientEmail: values.patient_email,
        filename: values.report_pdf.name,
      });
      scrollPageToTop();
    } catch {
      addToast({
        variant: 'danger',
        content: 'We could not submit your referral. Please try again in a moment.',
        autohide: false,
      });
    }
  };

  const formik = useFormik<HcpReferralFormValues>({
    initialValues: getInitialValues(),
    validationSchema: hcpReferralSchema,
    onSubmit: handleSubmit,
  });

  const handleSubmitAnother = () => {
    formik.resetForm();
    setPageState({ kind: 'normal' });
  };

  return (
    <div ref={pageRef} className='hcp-referral-page'>
      <div className='hcp-referral-page__header'>
        <HeaderNav partnerLogoSrc={BRIDGE_BIO_LOGO_SRC} noLoginButton />
      </div>

      <div className='hcp-referral-page__content' style={{ height: `calc(100vh - ${headerHeight}px)` }}>
        {pageState.kind === 'success' ? (
          <main className='hcp-referral-page__success-main'>
            <SubmissionSuccess
              submittedAt={pageState.submittedAt}
              patientEmail={pageState.patientEmail}
              filename={pageState.filename}
              onSubmitAnother={handleSubmitAnother}
            />
          </main>
        ) : (
          <main>
            <Hero />

            <section className='hcp-referral-page__form-band'>
              <div className='container-xxl'>
                <div className='hcp-referral-page__form-card'>
                  <FormikProvider value={formik}>
                    <IntakeForm />
                  </FormikProvider>
                </div>
              </div>
            </section>

            <ProcessBand
              heading='How review works, end to end'
              id='hcp-process'
              introEyebrow='From submission to result'
              introLead="Submissions go through internal review before any patient is contacted. Here's the full timeline so you and your patient know what to expect."
              steps={PROCESS_STEPS}
              variant='hcp'
            />
          </main>
        )}
        <div className='container-xxl'>
          <div className='hcp-referral-page__references'>
            <BridgeBioMaterialCode />
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};
